import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CultureService } from 'src/app/modules/core/services/culture.service';
import { Store } from '@ngrx/store';
import {
  ChannelUIds,
  CommunicationRecipientStatus,
  ConfirmationType,
  MassNotification,
  MassNotificationRecipient,
  MassNotificationRecipientStatResponseItem,
  MassNotificationRecipientStats
} from 'src/app/modules/communications/store/communications/communications.model';
import { CommunicationsApiService } from 'src/app/modules/communications/store/communications/communications-api.service';
import { ChannelSelectionService } from 'src/app/modules/communications/services/channel-selection-service';
import { View, Document, Workflow, WorkflowStep, ViewControlType, ViewComponent } from 'src/app/modules/core/store/models';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { DatasetApiService } from 'src/app/modules/core/services/dataset-api.service';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { cloneDeep } from 'lodash-es';
import { DataApiService } from 'src/app/modules/cop/services/data/data-api.service';
import { UserProfile, UserProfileApiService } from 'src/app/modules/core/store/user-profile';
import { UserApiService } from 'src/app/modules/management/store/users/user-api.service';
import { UserContextService } from 'src/app/modules/core/services/user-context.service';
import moment from 'moment';
import { NotificationStatusService } from 'src/app/modules/communications/services/notification-status-service';
import { TemplateDesignerService } from 'src/app/modules/communications/services/template-designer.service';
import { GUID_EMPTY } from '@app/core/constants';

@Component({
    selector: 'topo-comms-recipient-details',
    templateUrl: './recipient-details.component.html',
    styleUrls: ['./recipient-details.component.scss'],
    standalone: false
})
export class RecipientDetailsComponent implements OnInit, OnDestroy {
  @Input() set recipient(value: MassNotificationRecipient) {
    this._recipient = cloneDeep(value);
    this.selectedTabIndex = 0;
    this.dataset = null;
    if (!!this._recipient) {
      this.loadRecipientDetails(value);
    }
  }
  _recipient: MassNotificationRecipient;
  get recipient(): MassNotificationRecipient {
    return this._recipient;
  }

  @Input() notification: MassNotification;

  _recipientWorkflow: Workflow;
  @Input() set recipientWorkflow(value: Workflow) {
    this._recipientWorkflow = value;
  }

  get recipientWorkflow() {
    return this._recipientWorkflow;
  }

  @Output() recipientsDetailsClosed = new EventEmitter();

  dataset: Dataset;
  document: Document;
  editView: View;
  selectedViewContainer: ViewComponent;
  selectedViewControlContainerUId: string;
  viewControlContainers: ViewComponent[] = [];
  documentControls: any[] = [];
  selectedTabIndex: any;
  tabs: string[];
  guidEmpty = GUID_EMPTY;

  recipientStats: {
    channelUId: string;
    title: string;
    icon: string;
    address?: string;
    statusDetails?: string;
    responseText?: string;
    responseAddress?: string;
    responseTime?: string;
    confirmationOptionsUId?: string;
    status: CommunicationRecipientStatus;
  }[] = [];

  constructor(
    private store: Store<any>,
    private translateService: TranslateService,
    private commApiService: CommunicationsApiService,
    private channelSelectionService: ChannelSelectionService,
    private cultureService: CultureService,
    private notificationStatusService: NotificationStatusService,
    private templateDesignerService: TemplateDesignerService,
    private changeDetector: ChangeDetectorRef,
    private dataApiService: DataApiService
  ) {}

  ngOnInit(): void {
    this.tabs = [];
    this.tabs.push(this.translateService.instant('COMMUNICATIONS.TAB-STATUS'));
    this.tabs.push(this.translateService.instant('COMMUNICATIONS.TAB-ADDITIONAL-INFORMATION'));
  }

  ngOnDestroy(): void {}

  private loadRecipientDetails(recipient: MassNotificationRecipient): void {
    if (!!recipient.sourceDatasetUId && !!recipient.sourceDocumentUId) {
      this.dataApiService.getDataset(recipient.sourceDatasetUId, { includeWorkflow: true }).subscribe(dataset => {
        this.dataset = dataset;
        this.dataApiService.getDocument(recipient.sourceDatasetUId, recipient.sourceDocumentUId).subscribe(doc => {
          this.document = doc;
          this.editView = this.dataset?.workflow?.views?.find(f => f.name === 'detail');
          this.selectedViewControlContainerUId = null;

          if (this.editView?.controls?.some(c => c.controlType === ViewControlType.ViewControlContainer)) {
            this.viewControlContainers = this.editView.controls;
            this.selectedViewContainer = this.editView.controls.length > 0 ? this.editView.controls[0] : null;
            this.selectedViewControlContainerUId = this.selectedViewContainer?.uId;
          }
          this.changeDetector.markForCheck();
        });
      });
    }
    this.commApiService.getMassNotificationRecipientStats(this.notification?.uId, [recipient.uId], false, true).subscribe(resp => {
      const stats = resp?.find(r => r.massNotificationRecipientUId === recipient.uId);

      const recipientStats: {
        configiredChannelUId: string;
        channelUId: string;
        title: string;
        icon: string;
        address?: string;
        statusDetails?: string;
        responseText?: string;
        responseAddress?: string;
        responseTime?: string;
        confirmationOptionsUId?: string;
        status: CommunicationRecipientStatus;
      }[] = [];

      let singleRecipientChannel = this.channelSelectionService.getSingleRecipientChannel(recipient);

      if (!!singleRecipientChannel) {
        const channelItems = stats.channelItems?.filter(c => c.configuredChannelUId === singleRecipientChannel.uId);

        const status = this.notificationStatusService.getBestRecipientStatus(channelItems);

        const recipientStat = {
          configiredChannelUId: singleRecipientChannel.uId,
          channelUId: singleRecipientChannel.channelUId,
          title: singleRecipientChannel.name,
          address: '',
          statusDetails: null,
          icon: this.channelSelectionService.getChannelIcon(singleRecipientChannel.channelUId, false),
          status: status
        };

        recipientStats.push(recipientStat);
      } else {
        const allChannelConfiguredChannels = this.channelSelectionService.getUniqueConfiguredChannels(
          this?.notification?.channelsCascadeSettings
        );

        allChannelConfiguredChannels
          ?.filter(c => recipient.recipientAddresses?.find(a => a.configuredChannelUId === c.uId))
          ?.forEach(configuredChannel => {
            const isDM =
              (configuredChannel?.channelUId !== ChannelUIds.MicrosoftTeamsChannelUId &&
                configuredChannel?.channelUId !== ChannelUIds.SlackChannelUId) ||
              configuredChannel?.settings?.sendDirectMessages === true;

            if (isDM === true) {
              const channelItems = stats.channelItems?.filter(c => c.configuredChannelUId === configuredChannel.uId);
              channelItems.forEach(item => {
                const status = this.notificationStatusService.getBestRecipientStatus([item]);
                const displayStatusDetails = item.displayStatusDetails ?? null;
                const statusDetails = displayStatusDetails
                  ? this.translateService.instant('COMMUNICATIONS.ERRORS.' + displayStatusDetails)
                  : null;
                const recipientStat = {
                  configiredChannelUId: configuredChannel.uId,
                  channelUId: configuredChannel.channelUId,
                  title: configuredChannel.name,
                  address: !!item.address ? item.address : '',
                  statusDetails: statusDetails,
                  icon: this.channelSelectionService.getChannelIcon(configuredChannel.channelUId, true),
                  status: status
                };

                this.fillRecipientResponse(recipientStat, stats);
                recipientStats.push(recipientStat);
              });
            }
          });
      }

      this.recipientStats = recipientStats;
      this.changeDetector.markForCheck();
    });
  }

  private fillRecipientResponse(recipientStats: any, stats: MassNotificationRecipientStats): void {
    const response = stats.responses?.find(
      r => r.responseConfiguredChannelUId === recipientStats.configiredChannelUId && r.responseAddress === recipientStats.address
    );

    if (!!response) {
      recipientStats.responseText = response.responseText;
      recipientStats.responseAddress = response.responseAddress;
      recipientStats.responseTime = response.responseTime;
      recipientStats.confirmationOptionsUId = response.confirmationOptionsUId;
    }
  }

  onTabChanged(tabIndex: any): void {
    this.selectedTabIndex = tabIndex;
  }

  onWorkflowStepChanged(workflowStep: WorkflowStep): void {
    const updatedRecipient = {
      ...this._recipient,
      workflowStepUId: workflowStep.uId
    };

    this.recipient = updatedRecipient;

    this.store.dispatch(fromComm.updateMassNotificationRecipient({ recipient: updatedRecipient, close: false }));
  }

  closeClick(): void {
    this.recipientsDetailsClosed.emit();
  }

  public getDateDisplay(date: any): string {
    return this.cultureService.getDateDisplay(date, 'LONG-DATE-WITH-TIME');
  }

  public getDateDiff(date: any): string {
    return !!date ? moment(date).from(new Date()) : null;
  }

  public getResponseStatus(status: CommunicationRecipientStatus): string {
    return this.translateService.instant('COMMUNICATIONS.RECIPIENT-SEND-STATUS-' + status);
  }

  public getResponseStatusColor(status: CommunicationRecipientStatus): string {
    return this.notificationStatusService.getRecipientStatusColor(status);
  }

  public getResponseChannelIcon(channelUId: string): string {
    return this.channelSelectionService.getChannelIcon(channelUId);
  }

  public getResponseChannelName(channelUId: string): string {
    return this.channelSelectionService.getChannelName(channelUId);
  }

  public getConfirmationColor(response: MassNotificationRecipientStatResponseItem): string {
    if (!!response?.confirmationOptionsUId) {
      const confirmation = this.notification?.messageSettings?.confirmationButtons?.find(b => b.uId === response?.confirmationOptionsUId);
      if (!!confirmation) {
        return confirmation.type === ConfirmationType.Positive ? '#74B816' : '#E03131';
      }
    }
    return null;
  }

  public getConfirmationText(response: MassNotificationRecipientStatResponseItem): string {
    if (!!response?.confirmationOptionsUId) {
      const confirmation = this.notification?.messageSettings?.confirmationButtons?.find(b => b.uId === response?.confirmationOptionsUId);
      if (!!confirmation) {
        return this.templateDesignerService.getTranslateText(confirmation.text, this.cultureService.currentLang);
      }
    }
    return null;
  }

  public viewContainerChanged(event: any) {
    this.selectedViewControlContainerUId = event;
    const newContainer = this.viewControlContainers.find(c => c.uId === this.selectedViewControlContainerUId);

    this.selectedViewContainer = newContainer;
    this.changeDetector.detectChanges();
  }
}
